<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { responzaDebug } from 'shared/helpers/responzaDebug';
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP,
  ALLOWED_FILE_TYPES_FOR_LINE,
} from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton.vue';
import AIAssistanceButton from '../AIAssistanceButton.vue';
import { REPLY_EDITOR_MODES } from './constants';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'ReplyBottomPanel',
  components: { NextButton, FileUpload, VideoCallButton, AIAssistanceButton },
  mixins: [inboxMixin],
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    showEditorToggle: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    hasWhatsappTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    conversationDatabaseId: {
      type: Number,
      default: null,
    },
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    popoutReplyBox: {
      type: Boolean,
      default: false,
    },
    isScheduleDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'replaceText',
    'toggleInsertArticle',
    'toggleEditor',
    'selectWhatsappTemplate',
    'toggleMode',
    'togglePopout',
    'openScheduleModal',
  ],
  setup() {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();

    const uploadRef = ref(false);

    const keyboardEvents = {
      'Alt+KeyA': {
        action: () => {
          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uploadRef,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
      scheduledMessagesGetter:
        'scheduledMessages/getScheduledMessagesForConversation',
    }),
    isNote() {
      return this.mode === REPLY_EDITOR_MODES.NOTE;
    },
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    buttonClass() {
      return {
        warning: this.isNote,
      };
    },
    showAttachButton() {
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isALineChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    allowedFileTypes() {
      if (this.isATwilioWhatsAppChannel) {
        return ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP;
      }
      if (this.isALineChannel) {
        return ALLOWED_FILE_TYPES_FOR_LINE;
      }
      return ALLOWED_FILE_TYPES;
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    showMessageSignatureButton() {
      return !this.isOnPrivateNote;
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      // Signature functionality disabled by default
      return false;
      // return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    modeToggleIcon() {
      return this.mode === REPLY_EDITOR_MODES.NOTE 
        ? 'i-ph-arrow-bend-up-left' 
        : 'i-ph-note-blank';
    },
    modeToggleTooltip() {
      return this.mode === REPLY_EDITOR_MODES.NOTE
        ? this.$t('CONVERSATION.REPLYBOX.REPLY')
        : 'Nota privada';
    },
    expandIcon() {
      return this.popoutReplyBox ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2';
    },
    scheduledMessagesForConversation() {
      // Use database ID for filtering scheduled messages
      const idToFilter = this.conversationDatabaseId || this.conversationId;

      if (!this.scheduledMessagesGetter || !idToFilter) {
        responzaDebug('[ReplyBottomPanel] No getter or ID', {
          hasGetter: !!this.scheduledMessagesGetter,
          conversationId: this.conversationId,
          conversationDatabaseId: this.conversationDatabaseId,
          idToFilter
        });
        return [];
      }

      const allMessages = this.scheduledMessagesGetter(idToFilter);
      const filtered = allMessages?.filter(
        msg => msg.status === 'pending' || msg.status === 'failed'
      ) || [];

      responzaDebug('[ReplyBottomPanel] scheduledMessagesForConversation', {
        conversationId: this.conversationId,
        conversationDatabaseId: this.conversationDatabaseId,
        idToFilter,
        allMessages,
        filtered,
        count: filtered.length
      });

      return filtered;
    },
    hasScheduledMessages() {
      const result = this.scheduledMessagesForConversation.length > 0;
      responzaDebug('[ReplyBottomPanel] hasScheduledMessages', {
        count: this.scheduledMessagesForConversation.length,
        result
      });
      return result;
    },
    scheduleButtonVariant() {
      return this.hasScheduledMessages ? 'solid' : 'faded';
    },
    scheduleButtonColor() {
      return this.hasScheduledMessages ? 'amber' : 'slate';
    },
    scheduleButtonClass() {
      return [
        'mr-2 transition-all duration-200',
        this.hasScheduledMessages
          ? 'ring-2 ring-n-amber-9/40 shadow-[0_8px_24px_rgba(251,176,59,0.35)]'
          : '',
      ];
    },
    scheduleButtonIcon() {
      return this.hasScheduledMessages
        ? 'i-lucide-calendar-clock'
        : 'i-lucide-clock';
    },
  },
  watch: {
    conversationId: {
      handler(newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.fetchScheduledMessages();
        }
      },
    },
  },
  mounted() {
    ActiveStorage.start();
    this.fetchScheduledMessages();
  },
  methods: {
    async fetchScheduledMessages() {
      // Use display ID for API calls
      if (!this.conversationId) return;

      responzaDebug('[ReplyBottomPanel] fetchScheduledMessages called', {
        conversationId: this.conversationId,
        conversationDatabaseId: this.conversationDatabaseId
      });

      try {
        await this.$store.dispatch(
          'scheduledMessages/fetchForConversation',
          {
            conversationId: this.conversationId, // API expects display_id
            scheduleableId: this.conversationDatabaseId || this.conversationId,
          }
        );

        const idToCheck = this.conversationDatabaseId || this.conversationId;
        const messages = this.scheduledMessagesGetter?.(idToCheck);
        responzaDebug('[ReplyBottomPanel] fetchScheduledMessages complete', {
          conversationId: this.conversationId,
          conversationDatabaseId: this.conversationDatabaseId,
          idToCheck,
          messagesCount: messages?.length || 0,
          messages
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[ReplyBottomPanel] Failed to fetch scheduled messages', error);
      }
    },
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    replaceText(text) {
      this.$emit('replaceText', text);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
    handleModeToggle() {
      this.$emit('toggleMode');
    },
  },
};
</script>

<template>
  <div class="flex justify-between p-3" :class="wrapClass">
    <div class="left-wrap">
      <NextButton
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        icon="i-ph-smiley-sticker"
        slate
        faded
        sm
        @click="toggleEmojiPicker"
      />
      <NextButton
        v-tooltip.top-end="modeToggleTooltip"
        :icon="modeToggleIcon"
        slate
        faded
        sm
        @click="handleModeToggle"
      />
      <FileUpload
        ref="uploadRef"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        input-id="conversationAttachment"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="enableDragAndDrop"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
      >
        <NextButton
          v-if="showAttachButton"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          icon="i-ph-paperclip"
          slate
          faded
          sm
        />
      </FileUpload>
      <NextButton
        v-if="showAudioRecorderButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :icon="!isRecordingAudio ? 'i-ph-microphone' : 'i-ph-microphone-slash'"
        slate
        faded
        sm
        @click="toggleAudioRecorder"
      />
      <!-- RESPONZA: Botão de toggle do editor rico removido -->
      <!-- AVISO: Este botão estava com bug ao alternar entre editor rico e simples -->
      <!-- Editor rico está sempre ativo agora - mantido comentado para referência de outros sistemas -->
      <!-- <NextButton
        v-if="showEditorToggle"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
        icon="i-ph-quotes"
        slate
        faded
        sm
        @click="$emit('toggleEditor')"
      /> -->
      <NextButton
        v-if="showAudioPlayStopButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
        :icon="audioRecorderPlayStopIcon"
        slate
        faded
        sm
        :label="recordingAudioDurationText"
        @click="toggleAudioRecorderPlayPause"
      />
      <!-- <NextButton
        v-if="showMessageSignatureButton"
        v-tooltip.top-end="signatureToggleTooltip"
        icon="i-ph-signature"
        slate
        faded
        sm
        @click="toggleMessageSignature"
      /> -->
      <NextButton
        v-if="hasWhatsappTemplates"
        v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        sm
        @click="$emit('selectWhatsappTemplate')"
      />
      <VideoCallButton
        v-if="(isAWebWidgetInbox || isAPIInbox) && !isOnPrivateNote"
        :conversation-id="conversationId"
      />
      <!-- <AIAssistanceButton
        v-if="!isFetchingAppIntegrations"
        :conversation-id="conversationId"
        :is-private-note="isOnPrivateNote"
        :message="message"
        @replace-text="replaceText"
      /> -->
      <transition name="modal-fade">
        <div
          v-show="uploadRef && uploadRef.dropActive"
          class="fixed top-0 bottom-0 left-0 right-0 z-20 flex flex-col items-center justify-center w-full h-full gap-2 text-slate-900 dark:text-slate-50 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="text-2xl break-words text-slate-900 dark:text-slate-50">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
      <NextButton
        v-if="enableInsertArticleInReply"
        v-tooltip.top-end="$t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')"
        icon="i-ph-article-ny-times"
        slate
        faded
        sm
        @click="toggleInsertArticle"
      />
    </div>
    <div class="right-wrap">
      <NextButton
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.EXPAND')"
        :icon="expandIcon"
        slate
        faded
        sm
        class="mr-2"
        @click="$emit('togglePopout')"
      />
      <NextButton
        v-if="!isNote"
        v-tooltip.top-end="'Agendar mensagem'"
        :icon="scheduleButtonIcon"
        :variant="scheduleButtonVariant"
        :color="scheduleButtonColor"
        size="sm"
        :class="scheduleButtonClass"
        :disabled="isScheduleDisabled"
        @click="$emit('openScheduleModal')"
      />
      <woot-button
        size="small"
        :class-names="buttonClass"
        :is-disabled="isSendDisabled"
        @click="onSend"
      >
        {{ isNote ? 'Criar Nota' : sendButtonText }}
      </woot-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.left-wrap {
  @apply items-center flex gap-2;
}

.right-wrap {
  @apply flex;
}

::v-deep .file-uploads {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply dark:bg-slate-800 bg-slate-100;
  }
}
</style>
